import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components';
import './RegistrationPage.css';
import { registerUser } from '../../services/AuthService';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import AccountRoleSelector from '../../parent/components/AccountRoleSelector';
import '../../parent/components/AccountRoleSelector.css';
import ParentSignupForm from '../../parent/components/auth/ParentSignupForm';
import '../../parent/components/auth/ParentSignupForm.css';

function RegistrationPage() {
    const [selectedRole, setSelectedRole] = useState('student');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        agree: false,
    });

    const [errors, setErrors] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    // Reset scroll position to top when page loads
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });

        // Basic real-time validation (except for terms checkbox)
        let newErrors = { ...errors };
        if (name === 'firstName') {
            if (value.trim() === '') newErrors.firstName = 'First name is required';
            else delete newErrors.firstName;
        }
        if (name === 'lastName') {
            if (value.trim() === '') newErrors.lastName = 'Last name is required';
            else delete newErrors.lastName;
        }
        if (name === 'email') {
            if (!/\S+@\S+\.\S+/.test(value)) newErrors.email = 'Please enter a valid email';
            else delete newErrors.email;
        }
        // Only show terms error after submit attempt
        if (name === 'agree' && submitAttempted) {
            if (!checked) newErrors.agree = 'You must agree to the terms';
            else delete newErrors.agree;
        }
        setErrors(newErrors);
    };

    const [submitting, setSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitAttempted(true);
        setSubmitMessage(null);

        // Validate all fields
        let newErrors = {};
    if (formData.firstName.trim() === '') newErrors.firstName = 'First name is required';
    if (formData.lastName.trim() === '') newErrors.lastName = 'Last name is required';
        if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Please enter a valid email';
        if (!formData.agree) newErrors.agree = 'You must agree to the terms';
        setErrors(newErrors);
        if (Object.keys(newErrors).length) return;

        setSubmitting(true);
        try {
            // Track registration attempt
            try {
                UsageEventsTracker.recordUserAction('REGISTRATION_ATTEMPT', {
                    action: 'click',
                    category: 'auth',
                    entityType: 'button',
                    label: 'Desktop Registration Submit',
                    metadata: { email: formData.email.trim(), platform: 'desktop' }
                });
            } catch (trackErr) {
                console.warn('RegistrationPage: Failed to track registration attempt', trackErr);
            }

            // Attempt registration
            const regResult = await registerUser({
                fullName: `${formData.firstName} ${formData.lastName}`.trim(),
                email: formData.email,
                password: undefined // password will be set after email verification
            });
            if (!regResult.success) {
                // Friendly mapping for common API errors
                let friendly = regResult.message || 'Registration failed';
                const rawMsg = (regResult.message || '').toLowerCase();
                if (rawMsg.includes('already exists') || rawMsg.includes('409')) {
                    friendly = 'An account with that email already exists. You can sign in instead or reset your password if you forgot it.';
                }
                // Track registration error
                try {
                    UsageEventsTracker.recordUserAction('REGISTRATION_ERROR', {
                        action: 'error',
                        category: 'auth',
                        entityType: 'form',
                        label: 'Desktop Registration Failed',
                        metadata: { reason: rawMsg.includes('already') ? 'already_exists' : 'validation', platform: 'desktop' }
                    });
                } catch (trackErr) {
                    console.warn('RegistrationPage: Failed to track registration error', trackErr);
                }
                setSubmitMessage({ type: 'error', text: friendly });
                setSubmitting(false);
                return;
            }
            // Track registration success
            try {
                UsageEventsTracker.recordUserAction('REGISTRATION_SUCCESS', {
                    action: 'success',
                    category: 'auth',
                    entityType: 'form',
                    label: 'Desktop Registration Success',
                    metadata: { platform: 'desktop' }
                });
            } catch (trackErr) {
                console.warn('RegistrationPage: Failed to track registration success', trackErr);
            }
            // Show success message instructing email verification
            setSubmitMessage({ type: 'success', text: 'Registration successful! Check your email (and spam folder) to verify your account and set your password.' });
            setSubmitting(false);
            // Optionally, we could redirect to a "check your email" page instead
        } catch (err) {
            const raw = (err?.message || '').toLowerCase();
            let friendly = err.message || 'Unexpected error';
            if (raw.includes('already exists') || err?.status === 409) {
                friendly = 'An account with that email already exists. Try logging in or resetting your password.';
            }
            // Track registration error (network/unexpected)
            try {
                UsageEventsTracker.recordUserAction('REGISTRATION_ERROR', {
                    action: 'error',
                    category: 'auth',
                    entityType: 'form',
                    label: 'Desktop Registration Failed',
                    metadata: { reason: 'network', platform: 'desktop' }
                });
            } catch (trackErr) {
                console.warn('RegistrationPage: Failed to track registration error', trackErr);
            }
            setSubmitMessage({ type: 'error', text: friendly });
            setSubmitting(false);
        }
    };

    return (
        <div className="signupPage" style={{ backgroundImage: "url('/landing_page_hero/docuhoundsbackgroundclassroom.png')" }}>
            <Header />
            <div className="signupBgOverlay" aria-hidden="true"></div>

            <div className="signupCard">
                <section className="left">
                    <h2>Create your account</h2>
                    <p className="signupTagline">Scout will set up your study HQ and keep your semester under control.</p>
                    
                    <AccountRoleSelector selectedRole={selectedRole} onRoleChange={setSelectedRole} />

                    {selectedRole === 'parent' ? (
                      <ParentSignupForm />
                    ) : (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="formRowTwo">
                          <div className="formGroup">
                              <label htmlFor="firstName">First Name</label>
                              <input
                                  type="text"
                                  name="firstName"
                                  id="firstName"
                                  className={errors.firstName ? 'input error' : 'input'}
                                  value={formData.firstName}
                                  onChange={handleChange}
                                  placeholder="First name"
                                  required
                              />
                              {errors.firstName && <span className="errorMessage">{errors.firstName}</span>}
                          </div>
                          <div className="formGroup">
                              <label htmlFor="lastName">Last Name</label>
                              <input
                                  type="text"
                                  name="lastName"
                                  id="lastName"
                                  className={errors.lastName ? 'input error' : 'input'}
                                  value={formData.lastName}
                                  onChange={handleChange}
                                  placeholder="Last name"
                                  required
                              />
                              {errors.lastName && <span className="errorMessage">{errors.lastName}</span>}
                          </div>
                        </div>
                        
                        <div className="formGroup">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                className={errors.email ? 'input error' : 'input'}
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter your email"
                                required
                            />
                            {errors.email && <span className="errorMessage">{errors.email}</span>}
                            <div className="infoBox">
                              You'll set your password after verifying your email.
                            </div>
                        </div>

                        <div className="checkboxGroup">
                            <label className="checkboxLabel">
                                <input
                                    type="checkbox"
                                    name="agree"
                                    checked={formData.agree}
                                    onChange={handleChange}
                                />
                                <span className="checkmark"></span>
                                <span className="checkboxText">
                                    I agree to the{' '}
                                    <Link to="/terms" className="termsLink" target="_blank" rel="noopener noreferrer">
                                        Terms of Service
                                    </Link>{' '}
                                    and{' '}
                                    <Link to="/privacy" className="privacyLink" target="_blank" rel="noopener noreferrer">
                                        Privacy Policy
                                    </Link>
                                </span>
                            </label>
                            {errors.agree && submitAttempted && <span className="errorMessage checkboxError">{errors.agree}</span>}
                        </div>

                        <button
                            type="submit"
                            className="createAccountBtn"
                            disabled={submitting}
                        >{submitting ? 'Submitting…' : 'Create Account'}</button>
                        <p className="frictionKiller">Takes less than 30 seconds to get started</p>
                        
                        {submitMessage && (
                            <div
                                className={`submitStatus ${submitMessage.type === 'error' ? 'statusError' : 'statusSuccess'}`}
                                role={submitMessage.type === 'error' ? 'alert' : 'status'}
                                aria-live={submitMessage.type === 'error' ? 'assertive' : 'polite'}
                            >
                                <span>{submitMessage.text}</span>
                                {submitMessage.type === 'error' && submitMessage.text.toLowerCase().includes('already exists') && (
                                    <span className="statusLinks" style={{ display: 'block', marginTop: '6px' }}>
                                        <Link to="/login">Sign in</Link>
                                        <span className="statusLinksSeparator" aria-hidden="true">|</span>
                                        <Link to="/forgot-password">Reset Password</Link>
                                    </span>
                                )}
                            </div>
                        )}
                    </form>
                    )}

                    <div className="bottomDivider"></div>
                    <div className="loginPrompt">
                        Already have an account?{' '}
                        <Link to="/login" className="loginLink">
                            Sign in here
                        </Link>
                    </div>
                </section>

                <aside className="right">
                    <div className="welcome">
                        <div className="mascotFrame">
                            <img src="/scout_images/Scout_Thumbsup.png" alt="Scout welcoming you" className="mascotImg" />
                        </div>

                        <h2 className="welcomeTitle">Finally, a system that keeps up with your life.</h2>
                        <p className="welcomeBody">
                            Built for students balancing classes, work, and everything else.<br />
                            Upload your syllabus and everything falls into place.
                        </p>

                        <div className="bullets">
                            <div className="bullet"><span className="bulletIcon">✓</span><span><strong>Upload once</strong> and everything organizes automatically</span></div>
                            <div className="bullet"><span className="bulletIcon">✓</span><span><strong>See what actually matters</strong> today</span></div>
                            <div className="bullet"><span className="bulletIcon">✓</span><span><strong>Study from your real course material,</strong> not generic AI</span></div>
                        </div>

                        <div className="trustPill">
                            <span className="lock">🔒</span>
                            <span>Email verification • Privacy-first by default</span>
                        </div>

                        <div className="scoutLine" aria-hidden="true">"Let's build your semester plan."</div>
                    </div>

                    <img src="/scout_images/Scout_Thumbsup.png" className="watermark" alt="" aria-hidden="true" />
                </aside>
            </div>
        </div>
    );
}

export default RegistrationPage;